/**
 * Local (no-Firebase) backend.
 *
 * Implements the full backend contract against `localStore` so the product
 * can be built, demoed and manually verified while Firebase credentials are
 * unavailable. Enabled only in development builds; see
 * `common/firebase/core/firebaseConfig.js#resolveBackendMode`.
 */

import * as store from "./localStore";
import {
  assertCacheNamespace,
  buildCacheEntry,
  cacheDocId,
  normaliseCacheRead,
} from "./cachePolicy";

const now = () => Date.now();

function makeUid(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${now().toString(36)}`;
}

/* -------------------------------------------------- auth */

const authListeners = new Set();

function currentAuthUser() {
  return store.clone(store.getState().auth);
}

function emitAuth() {
  const user = currentAuthUser();
  authListeners.forEach((listener) => listener(user));
}

function setAuthUser(user) {
  store.update((db) => ({ ...db, auth: user }));
  emitAuth();
  return user;
}

function onAuthStateChange(callback) {
  authListeners.add(callback);
  // Match Firebase: emit the current value asynchronously on subscribe.
  const timer = setTimeout(() => callback(currentAuthUser()), 0);
  return () => {
    clearTimeout(timer);
    authListeners.delete(callback);
  };
}

async function signInAsGuest() {
  const existing = currentAuthUser();
  if (existing?.isAnonymous) return existing;

  return setAuthUser({
    uid: makeUid("guest"),
    isAnonymous: true,
    displayName: null,
    email: null,
    photoURL: null,
  });
}

/**
 * There is no Google sign-in without Firebase. We create a stable local
 * "signed-in" identity instead, clearly marked in the UI as local mode.
 */
async function signInWithGoogle() {
  return setAuthUser({
    uid: makeUid("local"),
    isAnonymous: false,
    displayName: "Local Tester",
    email: null,
    photoURL: null,
  });
}

async function upgradeGuestWithGoogle() {
  const existing = currentAuthUser();
  if (!existing) return signInWithGoogle();

  // Keep the same uid so progress carries over, exactly like linkWithPopup.
  return setAuthUser({
    ...existing,
    isAnonymous: false,
    displayName: existing.displayName ?? "Local Tester",
  });
}

async function signOut() {
  setAuthUser(null);
}

/* -------------------------------------------------- users */

async function getUserProfile(uid) {
  return store.clone(store.getState().users[uid] ?? null);
}

async function createUserProfile(profile) {
  store.update((db) => ({
    ...db,
    users: { ...db.users, [profile.uid]: profile },
  }));
  return store.clone(profile);
}

async function updateUserProfile(uid, patch) {
  store.update((db) => {
    const existing = db.users[uid];
    if (!existing) return db;
    return {
      ...db,
      users: { ...db.users, [uid]: applyPatch(existing, patch) },
    };
  });
}

function subscribeUserProfile(uid, callback) {
  const emit = () => callback(store.clone(store.getState().users[uid] ?? null));
  const timer = setTimeout(emit, 0);
  const unsubscribe = store.subscribe(emit);
  return () => {
    clearTimeout(timer);
    unsubscribe();
  };
}

/* -------------------------------------------------- learning */

async function listLearningRecords(uid) {
  return Object.values(store.clone(store.getState().learning[uid] ?? {}));
}

async function listFavourites(uid) {
  return Object.values(store.clone(store.getState().favourites?.[uid] ?? {}));
}

async function saveFavourite(uid, favourite) {
  store.update((db) => ({
    ...db,
    favourites: {
      ...db.favourites,
      [uid]: { ...(db.favourites?.[uid] ?? {}), [favourite.favouriteId]: favourite },
    },
  }));
}

async function removeFavourite(uid, favouriteId) {
  store.update((db) => {
    const mine = { ...(db.favourites?.[uid] ?? {}) };
    delete mine[favouriteId];
    return { ...db, favourites: { ...db.favourites, [uid]: mine } };
  });
}

async function saveLearningRecord(uid, record) {
  store.update((db) => ({
    ...db,
    learning: {
      ...db.learning,
      [uid]: { ...(db.learning[uid] ?? {}), [record.conceptId]: record },
    },
  }));
}

/* -------------------------------------------------- rooms */

async function listRoomRefs(uid) {
  return Object.values(store.clone(store.getState().roomRefs?.[uid] ?? {}));
}

async function saveRoomRef(uid, roomRef) {
  store.update((db) => ({
    ...db,
    roomRefs: {
      ...(db.roomRefs ?? {}),
      [uid]: {
        ...(db.roomRefs?.[uid] ?? {}),
        [roomRef.roomId]: roomRef,
      },
    },
  }));
}

async function createRoom(room) {
  store.update((db) => ({ ...db, rooms: { ...db.rooms, [room.roomId]: room } }));
  return store.clone(room);
}

async function getRoom(roomId) {
  return store.clone(store.getState().rooms[roomId] ?? null);
}

async function updateRoom(roomId, patch) {
  store.update((db) => {
    const existing = db.rooms[roomId];
    if (!existing) return db;
    return { ...db, rooms: { ...db.rooms, [roomId]: applyPatch(existing, patch) } };
  });
}

function subscribeRoom(roomId, callback) {
  const emit = () => callback(store.clone(store.getState().rooms[roomId] ?? null));
  const timer = setTimeout(emit, 0);
  const unsubscribe = store.subscribe(emit);
  return () => {
    clearTimeout(timer);
    unsubscribe();
  };
}

async function upsertParticipant(roomId, participant) {
  store.update((db) => {
    const roomParticipants = db.participants[roomId] ?? {};
    const existing = roomParticipants[participant.uid];
    return {
      ...db,
      participants: {
        ...db.participants,
        [roomId]: {
          ...roomParticipants,
          // applyPatch resolves `{ __increment: n }`, which the room
          // leaderboard relies on. Without it the sentinel object would be
          // written straight into the document.
          [participant.uid]: existing
            ? applyPatch(existing, participant)
            : applyPatch({}, participant),
        },
      },
    };
  });
}

async function getParticipant(roomId, uid) {
  return store.clone(store.getState().participants[roomId]?.[uid] ?? null);
}

function subscribeParticipants(roomId, callback) {
  const emit = () =>
    callback(Object.values(store.clone(store.getState().participants[roomId] ?? {})));
  const timer = setTimeout(emit, 0);
  const unsubscribe = store.subscribe(emit);
  return () => {
    clearTimeout(timer);
    unsubscribe();
  };
}

async function removeParticipant(roomId, uid) {
  store.update((db) => {
    const roomParticipants = { ...(db.participants[roomId] ?? {}) };
    delete roomParticipants[uid];
    return { ...db, participants: { ...db.participants, [roomId]: roomParticipants } };
  });
}

async function upsertGameParticipant(roomId, gameId, participant) {
  store.update((db) => {
    const roomGames = db.gameParticipants?.[roomId] ?? {};
    const gameParticipants = roomGames[gameId] ?? {};
    const existing = gameParticipants[participant.uid];
    return {
      ...db,
      gameParticipants: {
        ...(db.gameParticipants ?? {}),
        [roomId]: {
          ...roomGames,
          [gameId]: {
            ...gameParticipants,
            [participant.uid]: existing
              ? applyPatch(existing, participant)
              : applyPatch({}, participant),
          },
        },
      },
    };
  });
}

async function getGameParticipant(roomId, gameId, uid) {
  return store.clone(store.getState().gameParticipants?.[roomId]?.[gameId]?.[uid] ?? null);
}

/* -------------------------------------------------- predictions */

async function savePrediction(roomId, uid, prediction) {
  store.update((db) => ({
    ...db,
    predictions: {
      ...db.predictions,
      [roomId]: { ...(db.predictions[roomId] ?? {}), [uid]: prediction },
    },
  }));
}

async function getPrediction(roomId, uid) {
  return store.clone(store.getState().predictions[roomId]?.[uid] ?? null);
}

function subscribePredictions(roomId, callback) {
  const emit = () =>
    callback(Object.values(store.clone(store.getState().predictions[roomId] ?? {})));
  const timer = setTimeout(emit, 0);
  const unsubscribe = store.subscribe(emit);
  return () => {
    clearTimeout(timer);
    unsubscribe();
  };
}

async function saveGamePrediction(roomId, gameId, uid, prediction) {
  store.update((db) => {
    const roomGames = db.gamePredictions?.[roomId] ?? {};
    const gamePredictions = roomGames[gameId] ?? {};
    return {
      ...db,
      gamePredictions: {
        ...(db.gamePredictions ?? {}),
        [roomId]: {
          ...roomGames,
          [gameId]: { ...gamePredictions, [uid]: prediction },
        },
      },
    };
  });
}

async function getGamePrediction(roomId, gameId, uid) {
  return store.clone(store.getState().gamePredictions?.[roomId]?.[gameId]?.[uid] ?? null);
}

/* -------------------------------------------------- cache */

/**
 * The cache rides on the same `localStore` as everything else, so the test
 * suite and an offline development session exercise the real code path
 * rather than a stub. `.env.test` blanks the Firebase config precisely so
 * this is the implementation the suite runs against.
 *
 * Entries go through the identical `cachePolicy` validation as the
 * Firebase backend. That is the point: a payload the local backend accepts
 * is a payload real Firestore accepts, so an oversize value fails on a
 * developer's machine rather than in production.
 *
 * A large entry can exceed the browser's localStorage quota. `localStore`
 * already swallows that and keeps the value in memory for the session,
 * which is the right outcome for a cache: it degrades to no cache.
 */
async function readCache(namespace, key) {
  assertCacheNamespace(namespace);
  const entry = store.getState().cache?.[cacheDocId(namespace, key)] ?? null;
  return normaliseCacheRead(store.clone(entry));
}

async function writeCache(namespace, key, value, meta = {}) {
  const entry = buildCacheEntry({
    namespace,
    key,
    value,
    provider: meta.provider,
    fetchedAt: meta.fetchedAt,
    // Local mode has no real identity. Firestore rules require the author
    // uid to match the caller, so record whoever is signed in locally and
    // fall back to a marker that says plainly where the write came from.
    updatedBy: currentAuthUser()?.uid ?? "local-mode",
  });

  const id = cacheDocId(entry.namespace, entry.key);
  store.update((db) => ({ ...db, cache: { ...(db.cache ?? {}), [id]: entry } }));
}

/* -------------------------------------------------- helpers */

/**
 * Support the small subset of Firestore field transforms the app uses so
 * both backends behave identically. `{ footballIQ: { __increment: 10 } }`
 * mirrors Firestore's `increment(10)`.
 */
function applyPatch(existing, patch) {
  const next = { ...existing };
  Object.entries(patch).forEach(([key, value]) => {
    if (value && typeof value === "object" && "__increment" in value) {
      next[key] = Number(existing[key] ?? 0) + Number(value.__increment);
    } else {
      next[key] = value;
    }
  });
  return next;
}

store.wireCrossTabSync();

const localBackend = Object.freeze({
  id: "local",

  onAuthStateChange,
  signInAsGuest,
  signInWithGoogle,
  upgradeGuestWithGoogle,
  signOut,

  getUserProfile,
  createUserProfile,
  updateUserProfile,
  subscribeUserProfile,

  listLearningRecords,
  saveLearningRecord,

  listFavourites,
  saveFavourite,
  removeFavourite,

  listRoomRefs,
  saveRoomRef,
  createRoom,
  getRoom,
  updateRoom,
  subscribeRoom,
  upsertParticipant,
  getParticipant,
  subscribeParticipants,
  removeParticipant,
  upsertGameParticipant,
  getGameParticipant,

  savePrediction,
  getPrediction,
  subscribePredictions,
  saveGamePrediction,
  getGamePrediction,

  readCache,
  writeCache,
});

export default localBackend;
